import mixpanel from 'mixpanel-browser';

import config from 'config';

import { AppTracker, EventType, Layout, ShareType, StreamingPlatform, UserRole, UserSession } from 'types';

type UserProfile = Omit<UserSession, 'accessToken'>;

const events = {
  // Initialization
  init: ({ debug }: { debug: boolean }) => mixpanel.init(config.mixpanel.token, { debug }),
  // Include app version to all events
  setVersion: (appVersion: string) => mixpanel.register({ appVersion }),
  // Authentication
  identify: (id?: string) => mixpanel.identify(id),
  signIn: ({ provider, email, phoneNumber, username, name, image }: UserProfile) =>
    mixpanel.track('Sign in', {
      provider,
      email,
      phoneNumber,
      username,
      name,
      image,
      authenticated: true
    }),
  // Set Profile Information
  setProfileInfo: ({ provider, email, phoneNumber, username, name, image }: UserProfile) =>
    mixpanel.people.set({
      provider,
      email,
      phoneNumber,
      username,
      name,
      image,
      authenticated: true
    }),
  // Home Page
  openHomePage: () => mixpanel.track('Open Home Page'),
  // Home Page
  watchEvent: (eventCode: string) => mixpanel.track('Watch event', { eventCode }),
  // Setup panel
  openSetupPanel: () => mixpanel.track('Open Setup panel'),
  toggleMicrophoneOnSetupPanel: (enabled: boolean) => mixpanel.track('Toggle Microphone on Setup panel', { enabled }),
  toggleCameraOnSetupPanel: (enabled: boolean) => mixpanel.track('Toggle Camera on Setup panel', { enabled }),
  // Schedule panel
  openSchedulePanel: () => mixpanel.track('Open Shedule panel'),
  // Start event
  startEvent: () => mixpanel.track('Start event', undefined, { send_immediately: true }),
  startEventSuccess: (eventCode: string, membersOnly: boolean) =>
    mixpanel.track('Start event SUCCESS', { eventCode, membersOnly }),
  startEventFail: (error: Error) => mixpanel.track('Start event FAIL', { error }),
  // Schedule event
  scheduleEvent: () => mixpanel.track('Schedule event', undefined, { send_immediately: true }),
  scheduleEventSuccess: (eventCode: string, membersOnly: boolean) =>
    mixpanel.track('Schedule event SUCCESS', { eventCode, membersOnly }),
  scheduleEventFail: (error: Error) => mixpanel.track('Schedule event FAIL', { error }),
  // Start scheduled event
  startScheduledEvent: (eventCode: string) => mixpanel.track('Start Scheduled event', { eventCode }),
  startScheduledEventSuccess: (eventCode: string) => mixpanel.track('Start Scheduled event SUCCESS', { eventCode }),
  startScheduledEventFail: (eventCode: string, error: Error) =>
    mixpanel.track('Start Scheduled event FAIL', { eventCode, error }),
  // Raise hand
  raiseHand: (eventCode: string, authenticated: boolean) =>
    mixpanel.track('Raise hand', { eventCode, authenticated }, { send_immediately: true }),
  // Drop hand
  dropHand: (eventCode: string, authenticated: boolean) =>
    mixpanel.track('Drop hand', { eventCode, authenticated }, { send_immediately: true }),
  raiseHandSuccess: (eventCode: string) => mixpanel.track('Raise hand SUCCESS', { eventCode }),
  raiseHandFail: (eventCode: string, error: Error) => mixpanel.track('Raise hand FAIL', { eventCode, error }),
  getHandRaiseApproval: (eventCode: string) => mixpanel.track('Get Hand raise approval', { eventCode }),
  // Get Invited to stage
  getInvitedToStage: (eventCode: string) => mixpanel.track('Get invited to stage', { eventCode }),
  // Join the stage
  joinStage: (eventCode: string) => mixpanel.track('Join Stage', { eventCode }),
  // Live Event Page
  toggleMicrophoneOnControlPanel: (eventCode: string, enabled: boolean) =>
    mixpanel.track('Toggle Microphone on Control panel', { eventCode, enabled }),
  toggleCameraOnControlPanel: (eventCode: string, enabled: boolean) =>
    mixpanel.track('Toggle Camera on Control panel', { eventCode, enabled }),
  // Backstage
  toggleMicrophoneOnJoinPanel: (eventCode: string, enabled: boolean) =>
    mixpanel.track('Toggle Microphone on Join panel', { eventCode, enabled }),
  toggleCameraOnJoinPanel: (eventCode: string, enabled: boolean) =>
    mixpanel.track('Toggle Camera on Join panel', { eventCode, enabled }),
  // End event
  endEvent: (eventCode: string) => mixpanel.track('End event', { eventCode }, { send_immediately: true }),
  endEventSuccess: (eventCode: string) => mixpanel.track('End event SUCCESS', { eventCode }),
  endEventFail: (eventCode: string, error: Error) => mixpanel.track('End event FAIL', { eventCode, error }),
  // Sign Out
  signOut: () => mixpanel.track('Sign out', { authenticated: false }),

  subscribeToChannel: (channelId: string, channelName: string, source: AppTracker.ChannelSubscriptionSource) =>
    mixpanel.track('Subscribe to channel', { channelId, channelName, source }),
  subscribeToChannelSuccess: (channelId: string, channelName: string, source: AppTracker.ChannelSubscriptionSource) =>
    mixpanel.track('Subscribe to channel SUCCESS', { channelId, channelName, source }),
  subscribeToChannelFail: (
    channelId: string,
    channelName: string,
    source: AppTracker.ChannelSubscriptionSource,
    error: Error
  ) => mixpanel.track('Subscribe to channel FAIL', { channelId, channelName, source, error }),

  unsubscribeFromChannel: (channelId: string, channelName: string, source: AppTracker.ChannelUnsubscriptionSource) =>
    mixpanel.track('Unsubscribe from channel', { channelId, channelName, source }),
  unsubscribeFromChannelSuccess: (
    channelId: string,
    channelName: string,
    source: AppTracker.ChannelUnsubscriptionSource
  ) => mixpanel.track('Unsubscribe from channel SUCCESS', { channelId, channelName, source }),
  unsubscribeFromChannelFail: (
    channelId: string,
    channelName: string,
    source: AppTracker.ChannelUnsubscriptionSource,
    error: Error
  ) => mixpanel.track('Unsubscribe from channel FAIL', { channelId, channelName, source, error }),

  addToCalendar: (eventCode: string, source: AppTracker.AddToCalendarSource) =>
    mixpanel.track('Add scheduled event to calendar', { eventCode, source }),
  addToCalendarSuccess: (eventCode: string, source: AppTracker.AddToCalendarSource) =>
    mixpanel.track('Add scheduled event to calendar SUCCESS', { eventCode, source }),
  addToCalendarFail: (eventCode: string, source: AppTracker.AddToCalendarSource, error: Error) =>
    mixpanel.track('Add scheduled event to calendar FAIL', { eventCode, source, error }),

  openChannelPage: (channelName: string) => mixpanel.track('Open Channel Page', { channelName }),

  openEventDetailPage: (eventCode: string) => mixpanel.track('Open Event Detail Page', { eventCode }),

  openProfilePanel: () => mixpanel.track('Open Profile Panel'),

  openSidebar: () => mixpanel.track('Open Sidebar'),

  enableHandRaise: (eventCode: string) => mixpanel.track('Enable Hand Raise', { eventCode }),
  enableHandRaiseSuccess: (eventCode: string) => mixpanel.track('Enable Hand Raise SUCCESS', { eventCode }),
  enableHandRaiseFail: (eventCode: string, error: Error) =>
    mixpanel.track('Enable Hand Raise FAIL', { eventCode, error }),

  disableHandRaise: (eventCode: string) => mixpanel.track('Disable Hand Raise', { eventCode }),
  disableHandRaiseSuccess: (eventCode: string) => mixpanel.track('Disable Hand Raise SUCCESS', { eventCode }),
  disableHandRaiseFail: (eventCode: string, error: Error) =>
    mixpanel.track('Disable Hand Raise FAIL', { eventCode, error }),

  rejectHandRaise: (eventCode: string, requester: string) =>
    mixpanel.track('Reject hand-raise request', { eventCode, requester }),
  rejectHandRaiseSuccess: (eventCode: string, requester: string) =>
    mixpanel.track('Reject hand-raise request SUCCESS', { eventCode, requester }),
  rejectHandRaiseFail: (eventCode: string, requester: string, error: Error) =>
    mixpanel.track('Reject hand-raise request FAIL', { eventCode, requester, error }),

  approveHandRaise: (eventCode: string, requester: string) =>
    mixpanel.track('Approve hand-raise request', { eventCode, requester }),
  approveHandRaiseSuccess: (eventCode: string, requester: string) =>
    mixpanel.track('Approve hand-raise request SUCCESS', { eventCode, requester }),
  approveHandRaiseFail: (eventCode: string, requester: string, error: Error) =>
    mixpanel.track('Approve hand-raise request FAIL', { eventCode, requester, error }),

  inviteToStage: (eventCode: string, viewer: string) => mixpanel.track('Invite Viewer To Stage', { eventCode, viewer }),
  inviteToStageSuccess: (eventCode: string, viewer: string) =>
    mixpanel.track('Invite Viewer To Stage SUCCESS', { eventCode, viewer }),
  inviteToStageFail: (eventCode: string, viewer: string, error: Error) =>
    mixpanel.track('Invite Viewer To Stage FAIL', { eventCode, viewer, error }),

  declineToJoin: (eventCode: string, username: string) => mixpanel.track('Decline To Join', { eventCode, username }),
  declineToJoinSuccess: (eventCode: string, username: string) =>
    mixpanel.track('Decline To Join SUCCESS', { eventCode, username }),
  declineToJoinFail: (eventCode: string, username: string, error: Error) =>
    mixpanel.track('Decline To Join FAIL', { eventCode, username, error }),

  linkStreamingPlatform: (platform: StreamingPlatform, source: AppTracker.LinkStreamingPlatformSource) =>
    mixpanel.track('Link Streaming Platform', { platform, source }),

  shareEventLink: (eventCode: string, type: ShareType, source: AppTracker.ShareSource) =>
    mixpanel.track('Share Event Link', { eventCode, type, source }),

  generateInvitationLink: (eventCode: string, inviteeName: string, role: UserRole.MODERATOR | UserRole.SPEAKER) =>
    mixpanel.track('Generate An Invitation Link', { eventCode, inviteeName, role }),
  generateInvitationLinkSuccess: (
    eventCode: string,
    inviteeName: string,
    role: UserRole.MODERATOR | UserRole.SPEAKER
  ) => mixpanel.track('Generate An Invitation Link SUCCESS', { eventCode, inviteeName, role }),
  generateInvitationLinkFail: (
    eventCode: string,
    inviteeName: string,
    role: UserRole.MODERATOR | UserRole.SPEAKER,
    error: Error
  ) => mixpanel.track('Generate An Invitation Link FAIL', { eventCode, inviteeName, role, error }),

  openEventInfoPanel: (eventCode: string) => mixpanel.track('Open Info Panel In Event Page', { eventCode }),

  openStudioPanel: (eventCode: string) => mixpanel.track('Open Studio Panel In Event Page', { eventCode }),

  upgradePlan: (source: AppTracker.UpgradePlanSource) => mixpanel.track('Upgrade Button', { source }),

  editChannelDetails: (channelId: string) => mixpanel.track('Edit Channel Details', { channelId }),
  editChannelDetailsSuccess: (channelId: string) => mixpanel.track('Edit Channel Details SUCCESS', { channelId }),
  editChannelDetailsFail: (channelId: string, error: Error) =>
    mixpanel.track('Edit Channel Details FAIL', { channelId, error }),

  changeLayout: (eventCode: string, layout: Layout) => mixpanel.track('Change Layout', { eventCode, layout }),

  exitEvent: (eventCode: string) => mixpanel.track('Exit Button In Event Page', { eventCode }),

  leaveStage: (eventCode: string) => mixpanel.track('Leave Stage', { eventCode }),
  leaveStageSuccess: (eventCode: string) => mixpanel.track('Leave Stage SUCCESS', { eventCode }),
  leaveStageFail: (eventCode: string, error: Error) => mixpanel.track('Leave Stage FAIL', { eventCode, error }),

  // Past Event Page
  openPastEventDetailsPage: (eventCode: string) => mixpanel.track('Open Past Event Details Page', { eventCode }),
  playPastEventRecording: (eventCode: string) => mixpanel.track('Play Past Event Recording', { eventCode }),

  // UzbekITTV Landing Page
  openUzbekITTVLandingPage: () => mixpanel.track('Open UzbekITTV Landing Page'),

  // Become a member
  becomeMember: (channelId: string, channelName: string, authenticated: boolean) =>
    mixpanel.track('Become a Member', { channelId, channelName, authenticated }),
  becomeMemberWithPayme: ({
    source,
    authenticated,
    eventType,
    eventCode
  }: {
    source: AppTracker.BecomeMemberWithPaymeSource;
    authenticated: boolean;
    eventType?: EventType;
    eventCode?: string;
  }) => mixpanel.track('Become a Member With Payme', { source, authenticated, eventType, eventCode }),

  // Payme
  proceedToPayme: ({
    source,
    eventType,
    eventCode
  }: {
    source: AppTracker.BecomeMemberWithPaymeSource;
    eventType?: EventType;
    eventCode?: string;
  }) => mixpanel.track('Proceed to Payme', { source, eventType, eventCode }),

  // Become a member modal
  closeBecomeMemberModal: ({
    authenticated,
    eventType,
    eventCode
  }: {
    authenticated: boolean;
    eventType: EventType;
    eventCode: string;
  }) => mixpanel.track('Close Become a Member Modal', { authenticated, eventType, eventCode }),

  // Set Membership Price
  setMembershipPrice: (channelId: string, channelName: string, price: number) =>
    mixpanel.track('Set Membership Price', { channelId, channelName, price }),
  setMembershipPriceSuccess: (channelId: string, channelName: string, price: number) =>
    mixpanel.track('Set Membership Price SUCCESS', { channelId, channelName, price }),
  setMembershipPriceFail: (channelId: string, channelName: string, error: Error) =>
    mixpanel.track('Set Membership Price FAIL', { channelId, channelName, error }),

  enablePiPOnPastEvent: () => mixpanel.track('Enable PiP on Past Event'),

  resumeBlockedAudio: (eventCode: string) => mixpanel.track('Resume autoplay blocked audio', { eventCode })
};

export default events;
